"use client";

import { clsx } from "clsx";
import { useRef } from "react";
import type { KeyboardEvent } from "react";
import type { BudgetFlexibility } from "./quizConstants";

// Final copy from the budget-flexibility design handoff — do not reword.
const OPTIONS: ReadonlyArray<{
  value: BudgetFlexibility;
  title: string;
  helper: string;
}> = [
  {
    value: "max",
    title: "Up to my budget",
    helper: "Show my best matches, including great lodges that cost less.",
  },
  {
    value: "target",
    title: "Close to my budget",
    helper: "Only show lodges priced near this amount.",
  },
];

/**
 * Stacked-rows radio choice for what the budget number means.
 *
 * A refinement of the budget question, not a new question — it sits under the
 * slider's min/max labels, stays visible and enabled even while the budget is
 * unset, and never participates in the continue gate (the draft field always
 * has a value). SegmentedRadioGroup deliberately isn't reused: it renders a
 * horizontal label-only pill group and can't hold the per-option helper line.
 */
export function BudgetFlexibilityRows({
  value,
  onChange,
  disabled,
}: {
  value: BudgetFlexibility;
  onChange: (next: BudgetFlexibility) => void;
  disabled?: boolean;
}) {
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Radio-group keyboard contract: roving tabindex (only the selected row is
  // tabbable) and arrow keys both select and focus the neighbour, wrapping.
  function moveSelection(offset: number) {
    const index = OPTIONS.findIndex((option) => option.value === value);
    const nextIndex = (index + offset + OPTIONS.length) % OPTIONS.length;
    onChange(OPTIONS[nextIndex]!.value);
    rowRefs.current[nextIndex]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      moveSelection(1);
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      moveSelection(-1);
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="What your budget means"
      // md+: two equal columns (design handoff: header-over-photo README
      // §3) capped at the form column's own max-w-2xl (42rem) — this block
      // no longer sits inside the budget group's own 520px cap, see
      // QuizStepConstraints.tsx. md:mt-4: claws back the scroll that
      // the alignment change (QuizStepShell.tsx's md:pt-24) introduced on
      // constraints.
      className="mt-5 md:mt-4 flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-3 md:max-w-2xl"
    >
      {OPTIONS.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              rowRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={handleKeyDown}
            className={clsx(
              // md:py-[10px]: same scroll-clawback reason as the
              // container's md:mt-4 above.
              "flex items-start gap-3 rounded-input border px-4 py-[13px] md:py-[10px] text-left",
              // Colour-only feedback (no size/weight change) so selecting never
              // shifts layout; the selected edge reads 2px via an inset ring
              // rather than a wider border for the same reason.
              "transition-[border-color,background-color,box-shadow] duration-[180ms]",
              "focus-ring",
              "cursor-pointer disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50",
              selected
                ? "border-primary bg-canopy-50 shadow-[inset_0_0_0_1px_var(--bsa-canopy-700)]"
                : "border-border-strong bg-surface-muted hover:border-canopy-300",
            )}
          >
            <span
              aria-hidden="true"
              className={clsx(
                "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center self-start rounded-full border-2",
                selected ? "border-primary" : "border-bush-400",
              )}
            >
              {selected && <span className="h-[9px] w-[9px] rounded-full bg-primary" />}
            </span>
            <span className="flex flex-col">
              <span className="text-[15px] font-semibold leading-[1.3] text-text">
                {option.title}
              </span>
              {/* md:mt-0: same scroll-clawback reason as the
                  container's md:mt-4 above. */}
              <span className="mt-0.5 md:mt-0 text-[13px] font-medium leading-[1.4] text-text-muted">
                {option.helper}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
